import { Badge, Button, DatePicker, Header, Icon, Input, Loader, Select } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import 'assets/scuf/datatable/honeywell/theme.css';
import { statusOptionsMf, typeSituationMf } from 'models/rmamanufatura/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { FilterAtomMf, RMAMANUFATURA, cnpjMask } from 'resources/';
import { RMAFilterHistoryMf, RmaFilterModelMf, RmaModelMf, RmaModelLiteMf } from 'models';
import useFetch from 'helpers/hooks/useFetch';
import useUser from 'helpers/hooks/useUser';
import { formatDate } from 'helpers';
import { rmaStatusColorsMf, rmaItemsEquipmentMf } from 'models/rmamanufatura/detailsRma';
import { getColors } from 'helpers/getBadgeColors';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { RmaLiteMf } from 'resources/atoms/rmaManufaturaAtom';

function RmaSearch() {
    const navigate: NavigateFunction = useNavigate();
    const { t } = useTranslation(['rmamanufatura', 'common', 'home']);
    const [error, setError] = useState<string>('');
    const [reset, setReset] = useState(true);
    const itemsPerPage = ['5', '10', '15', '20'];
    const [dataRma, getRmaFilter, loadingRmas] = useFetch<RmaFilterModelMf, RmaFilterModelMf>({
        url: RMAMANUFATURA.getRmasManufacturing,
        method: 'POST',
        redirectOnError: true
    });
    const { user: userDetails } = useUser();
    const [rmaFilter, setRmaFilter] = useRecoilState<RmaFilterModelMf>(FilterAtomMf);
    const [rmaState, setRmaState] = useRecoilState<RmaModelLiteMf>(RmaLiteMf);
    const [call] = useState(false);
    const [historyValue, setHistoryValue] = useState<RMAFilterHistoryMf>();
    const [, setHistoric] = useState<string[]>([]);
    const [calendar, setCalendar] = useState<{ from: Date | null | ''; to: Date | null | '' }>({ from: null, to: null });
    const resetRmaState = useResetRecoilState(RmaLiteMf);

    const loadHistory = () => {
        const savedHistory = JSON.parse(localStorage.getItem('historicoPesquisa') || '[]');
        setHistoric(savedHistory);
        const newValue = historyValue?.serialNumber;
        if (newValue && !savedHistory.includes(newValue)) {
            const newHistory = [...savedHistory, newValue];
            setHistoric(newHistory);
            localStorage.setItem('historicoPesquisa', JSON.stringify(newHistory));
        }
    };

    async function getRmaFilterDetails(indice = 1) {
        await getRmaFilter({ ...rmaFilter, indice }, current => {
            setRmaFilter(old => ({ ...old, totalItems: current.data?.totalItems }));
        });
    }

    function handleCellClick(row: RmaModelMf) {
        navigate(`rmaManufacturing-detail/${row.id}`);
        setRmaState({ ...rmaState, id: row.id });
    }

    useEffect(() => {
        getRmaFilterDetails();
    }, [reset, call]);

    useEffect(() => {
        return () => {
            setRmaFilter({ indice: 1, itemsByPage: 10, rmaManufacturing: [] });
        };
    }, []);

    useEffect(() => {
        resetRmaState();
    }, []);

    return (
        <>
            {(loadingRmas && <Loader text={t('common:actions.Loading')} />) || (
                <>
                    <div>
                        <Header title={t('rmamanufatura:translation.RMAMF')} logo={false} menu={false}>
                            <div className="buttonsSearch">
                                <Button
                                    content={t('translation.CreatenewRMA')}
                                    type="primary"
                                    size="medium"
                                    iconPosition="right"
                                    icon={<Icon name="add" root="building" />}
                                    badge="20"
                                    onClick={() => navigate('new-rma')}
                                    description={t('translation.CreatenewRMA')}
                                />
                            </div>
                        </Header>
                        <div className="form4Columns">
                            <Input
                                placeholder="RMA"
                                label="RMA"
                                type="number"
                                onChange={(value: string) =>
                                    setRmaFilter(old => ({ ...old, id: isNaN(parseInt(value)) ? undefined : parseInt(value) }))
                                }
                                value={rmaFilter?.id?.toString() ?? ''}
                            />
                            <Input
                                placeholder={t('rmamanufatura:translation.SerialNumber')}
                                label={t('rmamanufatura:translation.SerialNumber')}
                                onChange={(event: any) => {
                                    setRmaFilter(old => ({ ...old, serialNumber: event }));
                                    setHistoryValue({ serialNumber: event });
                                    loadHistory();
                                }}
                                value={rmaFilter?.serialNumber?.toString() ?? ''}
                                list="historicoList"
                            />
                            <Select
                                className="SelectStyle"
                                label={t('common:translation.StatusRMA')}
                                placeholder={t('common:translation.StatusRMA')}
                                options={statusOptionsMf}
                                onChange={value =>
                                    setRmaFilter(old => ({
                                        ...old,
                                        statusId: isNaN(parseInt(value)) ? undefined : parseInt(value)
                                    }))
                                }
                                value={rmaFilter?.statusId ?? ''}
                            />
                            <Input
                                label={t('rmamanufatura:rma.order.NFNumber')}
                                placeholder={t('rmamanufatura:rma.order.NFNumber')}
                                onChange={value => setRmaFilter(old => ({ ...old, nfHoneywell: value }))}
                                value={rmaFilter?.nfHoneywell?.toString() ?? ''}
                            />
                        </div>
                        <div className="form4Columns">
                            <Select
                                className="SelectStyle"
                                label={t('common:status.Situation')}
                                placeholder={t('common:status.Situation')}
                                options={typeSituationMf}
                                value={rmaFilter?.situation!}
                                onChange={value =>
                                    setRmaFilter(old => ({ ...old, situation: value == 1 ? null : value.toString() }))
                                }
                            />
                            <Input
                                placeholder={t('rma.budget.WorkOrder')}
                                label={t('rma.budget.WorkOrder')}
                                onChange={value => setRmaFilter(old => ({ ...old, numberPV: value }))}
                                value={rmaFilter?.numberPV?.toString() ?? ''}
                            />
                            <Input
                                label={t('rma.order.Customer.InvoiceCNPJ')}
                                placeholder={t('rma.order.Customer.InvoiceCNPJ')}
                                value={cnpjMask(rmaFilter?.cnpjNota ?? '')}
                                onChange={value => {
                                    setRmaFilter(old => ({ ...old, cnpjNota: cnpjMask(value.toString()) }));
                                }}
                            />
                            <Select
                                className="SelectStyle"
                                label={t('common:translation.ItemperPage')}
                                placeholder={t('common:translation.ItemperPage')}
                                value={rmaFilter?.itemsByPage?.toString()}
                                options={itemsPerPage.map((e: string) => ({ text: e.toString(), value: e }))}
                                onChange={value => setRmaFilter(old => ({ ...old, itemsByPage: parseInt(value) }))}
                            />
                        </div>
                        {userDetails?.role == '1' || userDetails?.role == '2' ? (
                            <div className="form4Columns">
                                <Input
                                    label={t('home:consultCompany.companyName')}
                                    placeholder={t('home:consultCompany.companyName')}
                                    value={rmaFilter?.nameCompany ?? ''}
                                    onChange={value => {
                                        setRmaFilter(old => ({ ...old, nameCompany: value }));
                                    }}
                                />
                                <Input
                                    label={t('Cnpj')}
                                    placeholder={t('Cnpj')}
                                    value={cnpjMask(rmaFilter?.cnpjCompany ?? '')}
                                    onChange={value => {
                                        setRmaFilter(old => ({ ...old, cnpjCompany: cnpjMask(value.toString()) }));
                                    }}
                                />
                                <Input
                                    label={'GBE'}
                                    placeholder={'GBE'}
                                    onChange={value => setRmaFilter(old => ({ ...old, gbe: value }))}
                                    value={rmaFilter?.gbe?.toString() ?? ''}
                                />
                                <DatePicker
                                    closeOnSelection
                                    type="daterange"
                                    viewType="compact"
                                    label={t('common:translation.Period')}
                                    error={error}
                                    rangeValue={{
                                        from: calendar.from ?? '',
                                        to: calendar.to ?? ''
                                    }}
                                    displayFormat={'DD/MM/YYYY'}
                                    closeOnDocumentClick={true}
                                    onTextChange={value => setError(value)}
                                    onRangeSelect={({ to, from }) => {
                                        {
                                            if (!from || !to) return;
                                        }
                                        setCalendar({ from, to });
                                        const fixTimezone = (date: Date) => {
                                            const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
                                            return new Date(date.getTime() - tzOffsetMs);
                                        };
                                        const addEndOfDay = (date: Date) => {
                                            const endOfDay = new Date(date);
                                            endOfDay.setUTCHours(23, 59, 59, 999);
                                            return endOfDay;
                                        };
                                        const toFixed = addEndOfDay(fixTimezone(to));
                                        const fromFixed = fixTimezone(from);
                                        setRmaFilter(old => ({ ...old, dateStart: fromFixed, dateEnd: toFixed }));
                                    }}
                                />
                            </div>
                        ) : (
                            ''
                        )}
                        <div className="buttonsSearch">
                            <Button
                                type="secondary"
                                icon="badge-delete"
                                content={t('common:actions.ResetFilters')}
                                iconPosition="right"
                                onClick={() => {
                                    setRmaFilter(
                                        old =>
                                            ({
                                                indice: 1,
                                                itemsByPage: old.itemsByPage
                                            }) as RmaFilterModelMf
                                    );
                                    setReset(old => !old);
                                }}
                            />
                            <Button
                                type="primary"
                                icon="filter1"
                                content={t('common:actions.ApplyFilters')}
                                iconPosition="right"
                                onClick={() => getRmaFilterDetails()}
                            />
                        </div>
                    </div>
                    <div className="div-datatable">
                        <DataTable
                            data={dataRma?.data?.rmaManufacturing ?? []}
                            rows={rmaFilter?.itemsByPage}
                            onCellClick={e => handleCellClick(e.rowData)}
                        >
                            <DataTable.Column field="id" header={t('Id')} sortable />
                            <DataTable.Column
                                field="cnpjCompany"
                                renderer={e => <p>{cnpjMask(e.rowData.cnpjCompany || '')}</p>}
                                header={t('CNPJ')}
                            />
                            <DataTable.Column
                                field=" "
                                renderer={e => (
                                    <Badge color={getColors(e.rowData.status.id, rmaStatusColorsMf)}>
                                        {e.rowData.status.name}
                                    </Badge>
                                )}
                                header={t('common:translation.StatusRMA')}
                            />
                            <DataTable.Column
                                field="gbe"
                                header="GBE"
                                renderer={e => {
                                    const gbe = e.rowData.rmaItemsEquipmentManufacturing?.[0]?.gbe;
                                    return <p>{gbe ?? '—'}</p>;
                                }}
                            />
                            <DataTable.Column
                                field=" "
                                renderer={e => <p>{formatDate(e.rowData.createdAt)}</p>}
                                header={t('common:translation.Date')}
                            />
                            <DataTable.Pagination
                                activePage={rmaFilter?.indice}
                                totalItems={rmaFilter?.totalItems!}
                                itemsPerPage={rmaFilter?.itemsByPage}
                                disabledPages={[rmaFilter?.indice]}
                                onPageChange={pageNumber => {
                                    getRmaFilterDetails(pageNumber as number);
                                    setRmaFilter(old => ({ ...old, indice: parseInt(pageNumber.toString()) }));
                                }}
                            />
                        </DataTable>
                    </div>
                </>
            )}
        </>
    );
}

export default RmaSearch;
